import React, { useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Snackbar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PinterestIcon from "@mui/icons-material/Pinterest";
import EmailIcon from "@mui/icons-material/Email";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { getRecipeImage } from "../../constants";

export default function ShareDialog({ open, onClose, recipe, image }) {
  const [showCopyNotification, setShowCopyNotification] = useState(false);
  const theme = useTheme();

  // Use window location for genuine share URL
  const shareUrl = window.location.origin + `/recipe/${recipe?.id}`;
  const shareTitle = `Check out this recipe: ${recipe?.recipe_name}`;
  const shareImage = getRecipeImage(recipe?.recipe_image);

  const handleCopy = () => {
    navigator.clipboard.writeText(shareUrl);
    setShowCopyNotification(true);
  };

  const handleShare = (platform) => {
    // Check if native sharing is available
    if (navigator.share && platform === "native") {
      navigator
        .share({
          title: shareTitle,
          text: `I found this amazing recipe for ${recipe?.recipe_name}!`,
          url: shareUrl,
        })
        .catch((err) => console.error("Error sharing:", err));
      return;
    }

    // Platform specific sharing links
    const shareLinks = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
        shareUrl
      )}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(
        shareUrl
      )}&text=${encodeURIComponent(shareTitle)}`,
      whatsapp: `https://api.whatsapp.com/send?text=${encodeURIComponent(
        shareTitle + " " + shareUrl
      )}`,
      pinterest: `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(
        shareUrl
      )}&media=${encodeURIComponent(
        shareImage
      )}&description=${encodeURIComponent(shareTitle)}`,
      email: `mailto:?subject=${encodeURIComponent(
        shareTitle
      )}&body=${encodeURIComponent(
        "I thought you might enjoy this recipe: " + shareUrl
      )}`,
    };

    window.open(shareLinks[platform], "_blank", "width=600,height=400");
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            background:
              theme.palette.mode === "dark"
                ? theme.palette.background.paper + "F0"
                : "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(15px)",
            borderRadius: 3,
            boxShadow: theme.shadows[8],
            padding: 2,
            transition: "all 0.3s ease",
          },
        }}
        fullWidth
        maxWidth="sm"
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={2}
        >
          <DialogTitle
            sx={{ fontWeight: 600, color: theme.palette.primary.main }}
          >
            Share this Recipe
          </DialogTitle>
          <IconButton onClick={onClose} sx={{ color: theme.palette.grey[600] }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent>
          <Box
            display="flex"
            justifyContent="center"
            mb={3}
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: theme.shadows[3],
            }}
          >
            <img
              src={shareImage}
              alt={recipe?.recipe_name}
              style={{
                width: "100%",
                height: "250px",
                objectFit: "cover",
              }}
            />
          </Box>

          <Typography
            variant="h6"
            mb={2}
            sx={{ textAlign: "center", fontWeight: 500 }}
          >
            {recipe?.recipe_name}
          </Typography>

          <Box
            display="flex"
            justifyContent="space-around"
            mb={4}
            sx={{
              "& svg": {
                fontSize: 32,
                transition: "transform 0.2s",
                cursor: "pointer",
                "&:hover": {
                  transform: "scale(1.2)",
                },
              },
            }}
          >
            <IconButton
              onClick={() => handleShare("facebook")}
              sx={{ color: theme.palette.info.main }}
            >
              <FacebookIcon />
            </IconButton>
            <IconButton
              onClick={() => handleShare("twitter")}
              sx={{ color: theme.palette.info.main }}
            >
              <TwitterIcon />
            </IconButton>
            <IconButton
              onClick={() => handleShare("whatsapp")}
              sx={{ color: theme.palette.success.main }}
            >
              <WhatsAppIcon />
            </IconButton>
            <IconButton
              onClick={() => handleShare("pinterest")}
              sx={{ color: theme.palette.error.main }}
            >
              <PinterestIcon />
            </IconButton>
            <IconButton
              onClick={() => handleShare("email")}
              sx={{ color: theme.palette.text.secondary }}
            >
              <EmailIcon />
            </IconButton>
          </Box>

          <Typography variant="body1" mb={1} sx={{ fontWeight: 500 }}>
            Or copy the link below
          </Typography>

          <Box display="flex" alignItems="center">
            <TextField
              fullWidth
              value={shareUrl}
              variant="outlined"
              size="small"
              InputProps={{
                readOnly: true,
                sx: { borderTopRightRadius: 0, borderBottomRightRadius: 0 },
              }}
            />
            <Button
              variant="contained"
              endIcon={<ContentCopyIcon />}
              onClick={handleCopy}
              sx={{
                ml: 0,
                height: "40px",
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                boxShadow: 2,
              }}
            >
              Copy
            </Button>
          </Box>

          {navigator.share && (
            <Button
              variant="outlined"
              fullWidth
              onClick={() => handleShare("native")}
              sx={{ mt: 2 }}
            >
              Use Device Sharing
            </Button>
          )}
        </DialogContent>
      </Dialog>

      <Snackbar
        open={showCopyNotification}
        autoHideDuration={3000}
        onClose={() => setShowCopyNotification(false)}
        message="Link copied to clipboard!"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </>
  );
}
